import type { TypedQuery, TypedQueryCondition } from "@/services/testing";

/**
 * Compact renderers for a data step's assertion source.
 *
 * A step carries EITHER a structured `query` (server-compiled, validated against the org schema) or
 * a legacy hand-written `soql` string. `query` is the stronger evidence, so it is always read first
 * — see `TypedQuery` in services/testing/types.ts.
 */

function renderValue(value: TypedQueryCondition["value"]): string {
	if (value === null) return "null";
	if (Array.isArray(value)) return `(${value.join(", ")})`;
	// Reference tokens ($ref.Id, $ref.Account.Name, …) are rendered verbatim — they are the whole
	// point of the structured form and must not be quoted or coerced.
	return String(value);
}

export function formatTypedQueryCondition(c: TypedQueryCondition): string {
	return `${c.field} ${c.op} ${renderValue(c.value)}`;
}

/** `Account where Name = $ref.Name, limit 1` */
export function formatTypedQuery(query: TypedQuery): string {
	const parts = [query.sobject];
	const where = (query.where ?? []).map(formatTypedQueryCondition).join(" and ");
	if (where) parts.push(`where ${where}`);
	let out = parts.join(" ");
	if (query.orderBy?.field) {
		out += ` order by ${query.orderBy.field}${query.orderBy.direction ? ` ${query.orderBy.direction}` : ""}`;
	}
	if (typeof query.limit === "number") out += `, limit ${query.limit}`;
	return out;
}

function asTypedQuery(value: unknown): TypedQuery | null {
	if (!value || typeof value !== "object" || Array.isArray(value)) return null;
	const q = value as Partial<TypedQuery>;
	return typeof q.sobject === "string" ? (q as TypedQuery) : null;
}

/**
 * The one-line description of a data/assert step's query, or null when the step has neither form.
 * `query` wins over `soql` — a step that carries both was compiled from the typed form.
 */
export function describeQueryStep(step: unknown): string | null {
	if (!step || typeof step !== "object" || Array.isArray(step)) return null;
	const s = step as Record<string, unknown>;
	const typed = asTypedQuery(s.query);
	if (typed) return formatTypedQuery(typed);
	if (typeof s.soql === "string" && s.soql.trim()) return s.soql.trim();
	return null;
}

/** Every query description found across an IR body's setup + steps, in order, de-duplicated. */
export function collectQueryDescriptions(ir: unknown): string[] {
	if (!ir || typeof ir !== "object" || Array.isArray(ir)) return [];
	const body = ir as Record<string, unknown>;
	const rows = [
		...(Array.isArray(body.setup) ? body.setup : []),
		...(Array.isArray(body.steps) ? body.steps : []),
	];
	const out: string[] = [];
	for (const row of rows) {
		const described = describeQueryStep(row);
		if (described && !out.includes(described)) out.push(described);
	}
	return out;
}
